import { default as React } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Box,
} from '@material-ui/core';

interface NodejsApiOptions {
  node_version?: string;
  language?: string;
  package_manager?: string;
  test_framework?: string;
  orm_choice?: string;
  include_docker?: boolean;
  include_swagger?: boolean;
}

const DEFAULT_VALUES: NodejsApiOptions = {
  node_version: '20',
  language: 'TypeScript',
  package_manager: 'npm',
  test_framework: 'Jest',
  orm_choice: 'None',
  include_docker: true,
  include_swagger: true,
};

export const NodejsApiOptionsExtension = (
  props: FieldExtensionComponentProps<NodejsApiOptions>,
) => {
  const { onChange, rawErrors, required, formData } = props;

  const options: NodejsApiOptions = {
    ...DEFAULT_VALUES,
    ...formData,
  };

  const handleFieldChange = (field: keyof NodejsApiOptions, value: any) => {
    onChange({
      ...options,
      [field]: value,
    });
  };

  const hasErrors = rawErrors && rawErrors.length > 0;

  return (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Node Version */}
      <FormControl fullWidth required={required} error={hasErrors}>
        <InputLabel id="node-version-label">Node.js Version</InputLabel>
        <Select
          labelId="node-version-label"
          value={options.node_version || '20'}
          onChange={e => handleFieldChange('node_version', e.target.value)}
          label="Node.js Version"
        >
          <MenuItem value="18">Node.js 18 LTS</MenuItem>
          <MenuItem value="20">Node.js 20 LTS (Recommended)</MenuItem>
          <MenuItem value="22">Node.js 22 LTS</MenuItem>
        </Select>
        <FormHelperText>
          Select the Node.js runtime version for your API
        </FormHelperText>
      </FormControl>

      {/* Language */}
      <FormControl fullWidth required={required} error={hasErrors}>
        <InputLabel id="language-label">Language</InputLabel>
        <Select
          labelId="language-label"
          value={options.language || 'TypeScript'}
          onChange={e => handleFieldChange('language', e.target.value)}
          label="Language"
        >
          <MenuItem value="TypeScript">TypeScript (Recommended)</MenuItem>
          <MenuItem value="JavaScript">JavaScript</MenuItem>
        </Select>
        <FormHelperText>
          TypeScript provides better type safety and tooling support
        </FormHelperText>
      </FormControl>

      {/* Package Manager */}
      <FormControl fullWidth required={required} error={hasErrors}>
        <InputLabel id="package-manager-label">Package Manager</InputLabel>
        <Select
          labelId="package-manager-label"
          value={options.package_manager || 'npm'}
          onChange={e => handleFieldChange('package_manager', e.target.value)}
          label="Package Manager"
        >
          <MenuItem value="npm">npm</MenuItem>
          <MenuItem value="yarn">Yarn</MenuItem>
          <MenuItem value="pnpm">pnpm</MenuItem>
        </Select>
        <FormHelperText>
          Choose your preferred package manager for dependency management
        </FormHelperText>
      </FormControl>

      {/* Test Framework */}
      <FormControl fullWidth required={required} error={hasErrors}>
        <InputLabel id="test-framework-label">Test Framework</InputLabel>
        <Select
          labelId="test-framework-label"
          value={options.test_framework || 'Jest'}
          onChange={e => handleFieldChange('test_framework', e.target.value)}
          label="Test Framework"
        >
          <MenuItem value="Jest">Jest (Recommended)</MenuItem>
          <MenuItem value="Mocha">Mocha</MenuItem>
          <MenuItem value="Vitest">Vitest</MenuItem>
        </Select>
        <FormHelperText>
          Testing framework for unit and integration tests
        </FormHelperText>
      </FormControl>

      {/* ORM Choice */}
      <FormControl fullWidth required={required} error={hasErrors}>
        <InputLabel id="orm-choice-label">ORM/Database Layer</InputLabel>
        <Select
          labelId="orm-choice-label"
          value={options.orm_choice || 'None'}
          onChange={e => handleFieldChange('orm_choice', e.target.value)}
          label="ORM/Database Layer"
        >
          <MenuItem value="None">None</MenuItem>
          <MenuItem value="Prisma">Prisma</MenuItem>
          <MenuItem value="TypeORM">TypeORM</MenuItem>
          <MenuItem value="Sequelize">Sequelize</MenuItem>
        </Select>
        <FormHelperText>
          Optional ORM for database access (can be added later)
        </FormHelperText>
      </FormControl>

      {/* Include Docker */}
      <FormControl fullWidth>
        <FormControlLabel
          control={
            <Checkbox
              checked={options.include_docker ?? true}
              onChange={e =>
                handleFieldChange('include_docker', e.target.checked)
              }
              color="primary"
            />
          }
          label="Include Docker Configuration"
        />
        <FormHelperText>
          Generate Dockerfile and .dockerignore for containerization
        </FormHelperText>
      </FormControl>

      {/* Include Swagger */}
      <FormControl fullWidth>
        <FormControlLabel
          control={
            <Checkbox
              checked={options.include_swagger ?? true}
              onChange={e =>
                handleFieldChange('include_swagger', e.target.checked)
              }
              color="primary"
            />
          }
          label="Include Swagger/OpenAPI Documentation"
        />
        <FormHelperText>
          Add Swagger UI and OpenAPI specification for API documentation
        </FormHelperText>
      </FormControl>

      {hasErrors && (
        <FormHelperText error>{rawErrors?.join(', ')}</FormHelperText>
      )}
    </Box>
  );
};

export const NodejsApiOptionsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'NodejsApiOptions',
    component: NodejsApiOptionsExtension,
  }),
);
